<template>
  <section id="enterprise-plot">
    <div class="map-area">
      <div id="map">
        <plot-input v-if="plot" class="plot-input" :plot="plot" :map="map" />
      </div>
      <map-style-changer :map="map"></map-style-changer>
    </div>
  </section>
</template>

<script>
import * as turf from "@turf/turf";
import { createNamespacedHelpers } from "vuex";
import { mapGetters } from "vuex";
import Mapbox from "mapbox-gl";
import Colors from "@/config/Colors.js";
import { PlotEditMapSource, PlotEditMapLayer } from "@/config/Map.js";
import * as PlotUtil from "@/utils/PlotUtil";
import { List } from "immutable";
import MapStyleChanger from "@/components/buttons/MapStyleChanger";
import LocalDbService from "@/services/localDbService";

const { mapActions } = createNamespacedHelpers("enterprisePlot");

// Mixins
import { LocalizeMixin, LayerMixin } from "@/mixins";

// Components
import PlotInput from "./input/PlotInput";

export default {
  mixins: [LocalizeMixin, LayerMixin],
  components: {
    PlotInput,
    MapStyleChanger,
  },
  props: {
    plot_child_category_id: Object,
  },
  computed: {
    ...mapGetters({
      mapCenter: "enterpriseMapSetting/getCenter",
      mapZoom: "enterpriseMapSetting/getZoom",
      mapStyle: "enterpriseMapSetting/getStyle",
      defaultPosition: "enterpriseMapSetting/getDefaultPosition",
      plotPoints: "enterprisePlotEdit/getPoints",
      editMode: "enterprisePlotEdit/getEditMode",
    }),
  },
  data() {
    return {
      map: null,
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      zoom: 8,
      mapStyleLocal: null,
      // Plot
      grab: false, // 編集トリガー
      points: List(),
      plot: null,
      isStyleChange: false,
      doMapResize: false,
      unsubscribe: null,
    };
  },
  beforeDestroy() {
    this.unsubscribe();
  },
  watch: {
    mapStyle(val) {
      this.isStyleChange = true;
      this.map.setStyle(val);
      this.mapStyleLocal = val;
    },

    plotPoints() {
      this.drawPoints();
    },
  },
  created() {},
  async mounted() {
    this.mapStyleLocal = await LocalDbService.getMapStyle();
    let routerViewClass = document.getElementsByClassName("router-view");
    routerViewClass[0].style.padding = "0.0rem";

    this.$vs.loading({
      container: "#map",
      scale: 0.6,
    });

    this.$store.dispatch("enterpriseMapSetting/fetchDefaultPosition");

    Mapbox.accessToken = this.accessToken;
    let map = new Mapbox.Map({
      container: "map",
      style: this.mapStyleLocal,
      center: [this.mapCenter.lng, this.mapCenter.lat],
      zoom: this.$route.params.fromLiveMap
        ? this.mapZoom
        : this.defaultPosition.zoom,
    });
    map.addControl(new Mapbox.NavigationControl());
    let scale = new Mapbox.ScaleControl({
      maxWidth: 80,
      unit: "imperial",
    });
    map.addControl(scale);
    scale.setUnit("metric");
    this.map = map; // ここで紐付けないと addSource() 時にエラー発生
    map.on("load", this.mapLoaded);
    map.on("style.load", this.mapStyleLoad);

    this.unsubscribe = this.$store.subscribe((mutation, _) => {
      if (mutation.type === "TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE") {
        if (this.map && !this.doMapResize) {
          this.doMapResize = true;
          setTimeout(() => {
            this.map.resize();
            this.doMapResize = false;
          }, 1000);
        }
      }
    });
  },
  destroyed() {
    this.$store.dispatch("enterprisePlotEdit/clear");
  },
  methods: {
    ...mapActions(["getPlot"]),
    setupMapSources() {
      this.map.addSource(PlotEditMapSource.PlotEditPointSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      this.map.addSource(PlotEditMapSource.PlotEditPolygonSource.id, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    },
    setupLayers() {
      const color = Colors.getPlotSubCategoryColor(
        String(this.plot.child_category.id),
      );

      this.map.addLayer({
        id: PlotEditMapLayer.PlotEditCircleLayer.id,
        type: "circle",
        source: PlotEditMapSource.PlotEditPointSource.id,
        paint: {
          "circle-radius": 6,
          "circle-color": color,
        },
      });

      this.map.addLayer({
        id: PlotEditMapLayer.PlotEditSymbolLayer.id,
        type: "symbol",
        source: PlotEditMapSource.PlotEditPointSource.id,
        filter: ["==", "length", 1],
        layout: {
          "icon-image": "{icon}",
          "icon-allow-overlap": true,
        },
      });

      this.map.addLayer({
        id: PlotEditMapLayer.PlotEditPolygonLayer.id,
        type: "fill",
        source: PlotEditMapSource.PlotEditPolygonSource.id,
        paint: {
          "fill-color": color,
          "fill-opacity": 0.3,
        },
      });

      this.map.addLayer({
        id: PlotEditMapLayer.PlotEditLineLayer.id,
        type: "line",
        source: PlotEditMapSource.PlotEditPolygonSource.id,
        paint: {
          "line-color": color,
          "line-width": 2,
        },
      });
    },
    setupEvents() {
      this.map.off("mousemove", this.onMouseMove);
      this.map.off("touchmove", this.onTouchMove);
      this.map.off("mousedown", PlotEditMapLayer.PlotEditCircleLayer.id);
      this.map.off("touchstart", PlotEditMapLayer.PlotEditCircleLayer.id);
      //
      this.map.off("click");
      this.map.on("click", this.onMapClick);

      this.map.on("mousedown", PlotEditMapLayer.PlotEditCircleLayer.id, (e) => {
        e.preventDefault();
        this.grab = true;
        this.targetIndex = e.features[0].properties.index;
        this.map.on("mousemove", this.onPointMove);
        this.map.once("mouseup", (_) => {
          this.grab = false;
          this.targetIndex = null;
        });
      });

      this.map.on(
        "touchstart",
        PlotEditMapLayer.PlotEditCircleLayer.id,
        (e) => {
          e.preventDefault();
          this.grab = true;
          this.targetIndex = e.features[0].properties.index;
          this.map.on("touchmove", this.onPointMove);
          this.map.once("touchend", (_) => {
            this.grab = false;
            this.targetIndex = null;
          });
        },
      );
    },
    drawPoints() {
      this.isStyleChange = false;
      let featureCollection = [];
      for (let i = 0; i < this.plotPoints.length; i++) {
        let feature = PlotUtil.createEditFeatureForPoint(
          this.plotPoints[i],
          i,
          this.plot.child_category.id,
          this.plotPoints.length,
        );
        featureCollection.push(feature);
      }

      this.map.getSource(PlotEditMapSource.PlotEditPointSource.id).setData({
        type: "FeatureCollection",
        features: featureCollection,
      });

      let showPlotPoints = this.plotPoints;
      if (this.plotPoints.length > 2) {
        showPlotPoints = this.plotPoints.concat([this.plotPoints[0]]);
      }

      let data = PlotUtil.createEditFeatureForPolygon(showPlotPoints);
      this.map
        .getSource(PlotEditMapSource.PlotEditPolygonSource.id)
        .setData(data);
    },
    /**
     * MapBox:LifeCycle:Loaded
     *
     * @param map
     */
    mapLoaded() {},
    /**
     * MapBox:LifeCycle:StyleLoad
     *
     */
    mapStyleLoad() {
      if (this.isDefaultMapStyle(this.mapStyleLocal)) {
        let lang = this.getBrowserLanguage();
        let local = "name_" + lang;
        if (this.map) {
          let textLayerIds = this.getTextLayerIds();
          textLayerIds.forEach((id) => {
            this.map.setLayoutProperty(id, "text-field", ["get", local]);
          });
        }
      }

      // 編集の場合
      if (this.$route.params.id) {
        this.getPlot(this.$route.params.id).then((data) => {
          this.plot = data;

          let collections = this.plot.plot_points.map((e) => {
            return turf.point([e.longitude, e.latitude]);
          });
          let features = turf.featureCollection(collections);
          let center = turf.center(features);

          this.map.flyTo({
            center: center.geometry.coordinates,
            zoom: this.$route.params.fromLiveMap
              ? this.mapZoom
              : 11,
          });

          this.setupMapSources();
          this.setupLayers();
          this.setupEvents();
          if (this.isStyleChange) this.drawPoints();

          this.$vs.loading.close("#map > .con-vs-loading");
        });
      } else if (this.$route.params.child_category) {
        this.plot = {
          child_category: {
            id: this.$route.params.child_category,
          },
        };

        this.setupMapSources();
        this.setupLayers();
        this.setupEvents();
        if (this.isStyleChange) this.drawPoints();

        this.$vs.loading.close("#map > .con-vs-loading");
      }
    },
    onMapClick(e) {
      this.$store.dispatch("enterprisePlotEdit/setEditMode", 1);
      this.$store.dispatch("enterprisePlotEdit/addPoint", [
        e.lngLat.lng,
        e.lngLat.lat,
      ]);
    },
    onPointMove(e) {
      if (this.grab) {
        this.plotPoints[this.targetIndex] = [e.lngLat.lng, e.lngLat.lat];
        this.$store.dispatch("enterprisePlotEdit/setPoint", this.plotPoints);
      }
    },
  },
};
</script>

<style lang="sass" scoped>

#enterprise-plot
  .map-area
    width: 100%
    height: 100vh

  #map, .mapboxgl-plot
    width: 100%
    height: 100vh

  #map
    position: relative

    .plot-input
      position: absolute
      left: 0
      top: 0
      bottom: 0
      z-index: 9
</style>
